package fr.arcplex.network.model

// ---- Theme model ----
// Parsed manually from JsonObject: the API uses snake_case keys and a dotted
// variant name ("action.primary") that @Serializable would not map.

data class CoherenceTheme(
    val page: PageColors = PageColors(),
    val kinds: Kinds = Kinds(),
    val textStyles: TextStyles = TextStyles(),
    val variants: Variants = Variants()
) {
    companion object {
        // Fallback defaults = the values previously hardcoded in
        // CoherenceFilmInfoView/TVColors, so rendering is unchanged until a
        // successful fetch replaces them.
        val DEFAULT = CoherenceTheme(
            page = PageColors(
                background = "#0B0E12",
                surface = "#171B21",
                text = "#FFFFFF",
                muted = "#9EFFFFFF",
                accent = "#FFFFFF"
            ),
            kinds = Kinds(
                text = TextKind(
                    size = 17,
                    color = "#D1FFFFFF",
                    weight = "400",
                    align = "start"
                ),
                action = ActionKind(
                    textSize = 16,
                    textColor = "#FFFFFF",
                    borderColor = "#606265",
                    radius = 5,
                    paddingX = 14,
                    paddingY = 6,
                    bgColor = "#F247F5"
                ),
                rail = RailKind(
                    titleSize = 15,
                    titleColor = "#FFFFFF",
                    cardWidth = 69,
                    gap = 9
                )
            ),
            textStyles = TextStyles(
                display = TextStyleStyle(
                    size = 44,
                    weight = "600",
                    lineHeight = 1.06,
                    color = "#F2F5F8"
                ),
                body = TextStyleStyle(
                    size = 17,
                    weight = "400",
                    color = "#D3D4D4"
                )
            ),
            variants = Variants(
                actionPrimary = ActionVariant(
                    bgColor = "#12F373",
                    textColor = "#111111",
                    borderColor = "#FFFFFF",
                    weight = "600"
                )
            )
        )
    }
}

data class PageColors(
    val background: String = "",
    val surface: String = "",
    val text: String = "",
    val muted: String = "",
    val accent: String = ""
)

data class Kinds(
    val text: TextKind = TextKind(),
    val action: ActionKind = ActionKind(),
    val rail: RailKind = RailKind()
)

data class TextKind(
    val size: Int? = null,
    val color: String = "",
    val weight: String? = null,
    val align: String? = null
)

data class ActionKind(
    val textSize: Int? = null,
    val textColor: String = "",
    val borderColor: String = "",
    val radius: Int? = null,
    val paddingX: Int? = null,
    val paddingY: Int? = null,
    val bgColor: String = ""
)

data class RailKind(
    val titleSize: Int? = null,
    val titleColor: String = "",
    val cardWidth: Int? = null,
    val gap: Int? = null
)

data class TextStyles(
    val display: TextStyleStyle = TextStyleStyle(),
    val body: TextStyleStyle = TextStyleStyle()
)

data class TextStyleStyle(
    val size: Int? = null,
    val weight: String? = null,
    val lineHeight: Double? = null,
    val color: String = ""
)

data class Variants(
    val actionPrimary: ActionVariant = ActionVariant()
)

data class ActionVariant(
    val bgColor: String = "",
    val textColor: String = "",
    val borderColor: String = "",
    val weight: String? = null
)

data class CoherenceLabels(
    val lang: String = "",
    val rails: Map<String, String> = emptyMap(),
    val actions: Map<String, String> = emptyMap(),
    val slots: Map<String, String> = emptyMap(),
    val contentTypes: Map<String, String> = emptyMap()
) {
    companion object {
        // Fallback defaults = the values previously hardcoded in
        // CoherenceFilmInfoView (actionLabelDefaults, slotLabel when-branches).
        val DEFAULT = CoherenceLabels(
            lang = "fr",
            rails = emptyMap(),
            actions = mapOf(
                "play" to "Lecture",
                "trailer" to "Bande-annonce",
                "wishlist" to "Liste d'envies"
            ),
            slots = mapOf(
                "director" to "Réalisation",
                "casting" to "Avec"
            ),
            contentTypes = emptyMap()
        )
    }
}

/** Branding payload of the port: theme + labels, or the defaults when absent. */
data class Branding(
    val theme: CoherenceTheme = CoherenceTheme.DEFAULT,
    val labels: CoherenceLabels = CoherenceLabels.DEFAULT
)
